import React, { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Bot, Save, X, Key, Copy, Check } from 'lucide-react'

interface Agent {
  id: string
  type: string
  agent_name: string
  agent_description: string | null
  mcp_schema_url: string | null
  capabilities: string[]
  data_access: string[]
  is_public: boolean
  status: string
  created_at: string
}

interface AgentRegistrationFormProps {
  tenantId: string
  getHeaders: () => HeadersInit
}

const AGENT_TYPES = [
  { value: 'agent', label: 'AI Agent' },
  { value: 'mcp_server', label: 'MCP Server' },
  { value: 'service_account', label: 'Service Account' },
  { value: 'human', label: 'Human Operator' }
]

const TYPE_COLORS: Record<string, string> = {
  agent: 'bg-blue-100 text-blue-800',
  mcp_server: 'bg-purple-100 text-purple-800',
  service_account: 'bg-green-100 text-green-800',
  human: 'bg-gray-100 text-gray-800'
}

export default function AgentRegistrationForm({ tenantId, getHeaders }: AgentRegistrationFormProps) {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)
  const [saving, setSaving] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Form state
  const [form, setForm] = useState({
    type: 'agent',
    agent_name: '',
    agent_description: '',
    endpoint_url: '',
    mcp_schema_url: '',
    capabilities: '',
    data_access: '',
    is_public: true,
    status: 'active'
  })

  useEffect(() => {
    loadAgents()
  }, [tenantId])

  async function loadAgents() {
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/registry/agents', { headers: getHeaders() })
      if (!res.ok) throw new Error('Failed to load agents')
      const data = await res.json()
      setAgents(data.agents ?? [])
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setEditingAgent(null)
    setForm({ type: 'agent', agent_name: '', agent_description: '', mcp_schema_url: '', capabilities: '', data_access: '', is_public: true, status: 'active' })
    setShowForm(true)
  }

  function openEdit(agent: Agent) {
    setEditingAgent(agent)
    setForm({
      type: agent.type,
      agent_name: agent.agent_name,
      agent_description: agent.agent_description ?? '',
      endpoint_url: (agent as any).endpoint_url ?? '',
      mcp_schema_url: agent.mcp_schema_url ?? '',
      capabilities: (agent.capabilities ?? []).join(', '),
      data_access: (agent.data_access ?? []).join(', '),
      is_public: agent.is_public,
      status: agent.status
    })
    setShowForm(true)
  }

  async function saveAgent() {
    if (!form.agent_name.trim()) return
    setSaving(true)
    try {
      const body = {
        ...( editingAgent ? { id: editingAgent.id } : {} ),
        type: form.type,
        agent_name: form.agent_name.trim(),
        agent_description: form.agent_description.trim() || null,
        endpoint_url: form.endpoint_url.trim() || null,
        mcp_schema_url: form.mcp_schema_url.trim() || null,
        capabilities: form.capabilities.split(',').map(s => s.trim()).filter(Boolean),
        data_access: form.data_access.split(',').map(s => s.trim()).filter(Boolean),
        is_public: form.is_public,
        status: form.status
      }

      const method = editingAgent ? 'PUT' : 'POST'
      const res = await fetch('/api/registry/agents', {
        method,
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error ?? 'Save failed')
      }
      setShowForm(false)
      await loadAgents()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  async function deleteAgent(id: string) {
    if (!confirm('Delete this agent registration?')) return
    try {
      const res = await fetch(`/api/registry/agents?id=${id}`, { method: 'DELETE', headers: getHeaders() })
      if (!res.ok) throw new Error('Delete failed')
      await loadAgents()
    } catch (e: any) {
      setError(e.message)
    }
  }

  function copyId(id: string) {
    navigator.clipboard.writeText(id)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Agent Registry</h2>
          <p className="text-sm text-gray-500 mt-1">Register AI agents, MCP servers, and service accounts for public discovery</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <Plus className="h-4 w-4 mr-2" />
          Register Agent
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      )}

      {/* Registration Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">{editingAgent ? 'Edit Agent' : 'Register New Agent'}</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {AGENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name *</label>
            <input
              type="text"
              value={form.agent_name}
              onChange={e => setForm(f => ({ ...f, agent_name: e.target.value }))}
              placeholder="e.g. SecureBot v2"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.agent_description}
              onChange={e => setForm(f => ({ ...f, agent_description: e.target.value }))}
              rows={3}
              placeholder="What does this agent do?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invocation Endpoint URL</label>
            <input
              type="url"
              value={form.endpoint_url}
              onChange={e => setForm(f => ({ ...f, endpoint_url: e.target.value }))}
              placeholder="https://myagent.example.com/api/invoke"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Where callers should send requests after receiving a trust token from the registry</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MCP Schema URL</label>
            <input
              type="url"
              value={form.mcp_schema_url}
              onChange={e => setForm(f => ({ ...f, mcp_schema_url: e.target.value }))}
              placeholder="https://example.com/.well-known/mcp.json"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Capabilities</label>
            <input
              type="text"
              value={form.capabilities}
              onChange={e => setForm(f => ({ ...f, capabilities: e.target.value }))}
              placeholder="read_files, send_email, web_search (comma-separated)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Access Scope</label>
            <input
              type="text"
              value={form.data_access}
              onChange={e => setForm(f => ({ ...f, data_access: e.target.value }))}
              placeholder="customer_pii, financial_records (comma-separated)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={e => setForm(f => ({ ...f, is_public: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">List publicly in registry</span>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={saveAgent}
              disabled={saving || !form.agent_name.trim()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : (editingAgent ? 'Update Agent' : 'Register Agent')}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Agents Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading agents...</div>
      ) : agents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
          <Bot className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No agents registered yet.</p>
          <button onClick={openCreate} className="mt-3 text-blue-600 text-sm hover:underline">Register your first agent</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Agent</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Capabilities</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Public</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.map(agent => (
                <tr key={agent.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{agent.agent_name}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-xs text-gray-400 font-mono">{agent.id.substring(0, 8)}...</span>
                      <button onClick={() => copyId(agent.id)} className="text-gray-400 hover:text-gray-600">
                        {copiedId === agent.id ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${TYPE_COLORS[agent.type] ?? 'bg-gray-100 text-gray-800'}`}>
                      {agent.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(agent.capabilities ?? []).slice(0, 3).map(cap => (
                        <span key={cap} className="bg-blue-50 text-blue-700 text-xs px-1.5 py-0.5 rounded">{cap}</span>
                      ))}
                      {(agent.capabilities ?? []).length > 3 && (
                        <span className="text-gray-400 text-xs">+{agent.capabilities.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${agent.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                      {agent.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${agent.is_public ? 'text-green-600' : 'text-gray-400'}`}>
                      {agent.is_public ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(agent)} className="text-gray-400 hover:text-blue-600">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteAgent(agent.id)} className="text-gray-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* API Key Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Key className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-gray-700 mb-2">Querying the Registry via API</p>
            <p className="text-gray-500 mb-3">External agents can query your registered agents. Free (basic) tier needs no auth. Higher tiers require an API key:</p>
            <div className="bg-gray-900 text-green-400 rounded-lg p-3 font-mono text-xs space-y-1 overflow-x-auto">
              <div># Free — no auth</div>
              <div>GET /api/registry/query?agentId={"<id>"}&tier=basic</div>
              <div className="mt-2"># Metered — requires API key (Settings → API Keys)</div>
              <div>GET /api/registry/query?agentId={"<id>"}&tier=capability</div>
              <div>Authorization: Bearer tc_your_api_key</div>
              <div className="mt-2"># MCP protocol</div>
              <div>POST /api/registry/mcp</div>
              <div>{'{"jsonrpc":"2.0","method":"tools/list","id":1}'}</div>
            </div>
            <p className="text-gray-500 mt-2">Generate an API key in <strong>Settings → API Keys</strong>.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
